/**
 * GA4 Analytics utility — central event tracking helpers.
 *
 * Every function guards with `typeof window !== 'undefined' && window.gtag`.
 * If GA isn't loaded (no consent, SSR, ad-blocker), they're silent no-ops.
 */

// ---------------------------------------------------------------------------
// Base helpers
// ---------------------------------------------------------------------------

function gtag(...args: unknown[]) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

export function trackEvent(
  name: string,
  params?: Record<string, unknown>,
) {
  gtag('event', name, params);
}

/** Sets user_id and user_role custom dimension for GA4. */
export function setUserProperties(user: {
  sub?: string;
  id?: string;
  permissions?: string[];
} | null) {
  if (!user) return;

  const userId = user.sub || user.id;
  if (userId) {
    gtag('set', { user_id: userId });
  }

  // Derive role from permission markers
  let role = 'guest';
  const perms = user.permissions ?? [];
  if (perms.includes('admin:admin')) role = 'admin';
  else if (perms.includes('pro:pro')) role = 'pro';
  else if (perms.includes('free:free')) role = 'free';

  gtag('set', 'user_properties', { user_role: role });
}

// ---------------------------------------------------------------------------
// Company events
// ---------------------------------------------------------------------------

export function trackCompanyView(euId: string, companyName: string) {
  trackEvent('company_view', {
    company_eu_id: euId,
    company_name: companyName,
  });
}

export function trackFileDownload(
  euId: string,
  fileType: string,
  fileId: number,
) {
  trackEvent('file_download', {
    company_eu_id: euId,
    file_type: fileType,
    file_id: fileId,
  });
}

export function trackTrendTabChange(
  metric: string,
  userType?: 'logged_in' | 'guest',
) {
  trackEvent('trend_tab_change', { metric, user_type: userType });
}

export function trackCompanyLinkClick(
  euId: string,
  linkType: string,
) {
  trackEvent('company_link_click', {
    company_eu_id: euId,
    link_type: linkType,
  });
}

export function trackSectionExpand(section: string, isOpen: boolean) {
  trackEvent('section_expand', { section, is_open: isOpen });
}

// ---------------------------------------------------------------------------
// Search events
// ---------------------------------------------------------------------------

export function trackSearchPerformed(params: {
  query: string;
  filterCount: number;
  resultCount: number;
}) {
  trackEvent('search_performed', {
    search_query: params.query,
    filter_count: params.filterCount,
    result_count: params.resultCount,
  });
}

export function trackSearchResultClick(euId: string, position: number) {
  trackEvent('search_result_click', {
    company_eu_id: euId,
    position,
  });
}

export function trackSearchSortChange(sort: string) {
  trackEvent('search_sort_change', { sort_field: sort });
}

export function trackSearchPagination(offset: number) {
  trackEvent('search_pagination', { offset });
}

// ---------------------------------------------------------------------------
// Homepage events
// ---------------------------------------------------------------------------

export function trackHomepageSearch(query: string) {
  trackEvent('homepage_search', { search_query: query });
}

export function trackHomepageButtonClick(button: string) {
  trackEvent('homepage_button_click', { button_name: button });
}

// ---------------------------------------------------------------------------
// Header navigation events
// ---------------------------------------------------------------------------

export function trackHeaderNavClick(
  button: string,
  userType: 'logged_in' | 'guest',
) {
  trackEvent('header_nav_click', {
    button_name: button,
    user_type: userType,
  });
}

// ---------------------------------------------------------------------------
// Auth events
// ---------------------------------------------------------------------------

export function trackLogin(method: string) {
  trackEvent('login', {
    method,
    transport_type: 'beacon',
  });
}

export function trackSignup(method: string) {
  trackEvent('sign_up', {
    method,
    transport_type: 'beacon',
  });
}
